import { useEffect, useState } from 'react'
import { CheckCircle, OpenNew, Retry, SyncProgress, WarningTriangle } from '@/assets/icons'
import { AnimatedEllipsis } from '@/components/animations/AnimatedEllipsis'
import { DotLoader } from '@/components/animations/DotLoader'
import { Modal, ModalContent } from '@/components/modal/Modal'
import { ModalFooterWithActions } from '@/components/modal/ModalFooterWithActions'
import { DocumentFile } from './DocumentFile'
import { ApplyTemplateStatus } from './types'

const APPLY_TEMPLATE_STEP_MESSAGES = ['Applying headers & footers', 'Inserting tables', 'Finalizing structure'] as const

/** How long each progress line stays visible before advancing to the next. */
const APPLY_TEMPLATE_STEP_ROTATE_MS = 2800

function useApplyingTemplateStepIndex(isActive: boolean) {
  const [index, setIndex] = useState(0)

  useEffect(() => {
    if (!isActive) {
      setIndex(0)
      return
    }
    const intervalId = window.setInterval(() => {
      setIndex((i) => (i + 1) % APPLY_TEMPLATE_STEP_MESSAGES.length)
    }, APPLY_TEMPLATE_STEP_ROTATE_MS)
    return () => window.clearInterval(intervalId)
  }, [isActive])

  return index
}

export type ApplyingTemplateModalProps = {
  /** whether the modal is open */
  open: boolean
  /** the status of the template application */
  status: ApplyTemplateStatus
  /** the name of the template */
  templateName: string
  /** the name of the output file */
  outputFileName: string
  /** the path of the applied docx file */
  outputFileId?: string
  /** Text shown in the success row. */
  successLabel: string
  /** the function to dismiss the modal */
  onDismiss: () => void
  /** the function to cancel the modal */
  onCancel: () => void
  /** when provided, a "View File" button is shown that calls this on click */
  onViewFile?: () => void
  /** error state: retry the template application */
  onRetry?: () => void
  /** In the case of error state, support saving the document without the template formatting. */
  onSaveWithoutTemplate?: () => void
  /** In the case of error state, support downloading the document without the template formatting. */
  onDownloadWithoutTemplate?: () => void
}

export function ApplyingTemplateModal({
  open,
  status,
  templateName,
  outputFileName,
  outputFileId,
  successLabel,
  onDismiss,
  onCancel,
  onViewFile,
  onRetry,
  onSaveWithoutTemplate,
  onDownloadWithoutTemplate,
}: ApplyingTemplateModalProps) {
  const isComplete = status === ApplyTemplateStatus.COMPLETE
  const isApplying = status === ApplyTemplateStatus.APPLYING
  const isApplyingActive = open && isApplying
  const stepIndex = useApplyingTemplateStepIndex(isApplyingActive)

  if (status === ApplyTemplateStatus.ERROR) {
    return (
      <Modal
        open={open}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) onDismiss()
        }}
      >
        <ModalContent
          title="Template couldn't be applied"
          titleIcon={<WarningTriangle className="h-6 w-6 text-red-700" aria-hidden />}
          height="max-sm"
          footer={
            <ModalFooterWithActions
              secondaryAction={
                onSaveWithoutTemplate
                  ? { label: 'Save without template', onClick: onSaveWithoutTemplate }
                  : onDownloadWithoutTemplate
                  ? { label: 'Download without template', onClick: onDownloadWithoutTemplate }
                  : undefined
              }
              primaryAction={{
                icon: <Retry className="h-4 w-4 shrink-0" aria-hidden />,
                label: 'Try again',
                onClick: () => onRetry?.(),
              }}
            />
          }
        >
          <p className="text-base font-normal leading-6 tracking-[-0.32px] text-brand-neutral-950">
            There was a problem formatting your document with the selected template. You can retry or continue without template formatting.
          </p>
        </ModalContent>
      </Modal>
    )
  }

  return (
    <Modal
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onDismiss()
      }}
    >
      <ModalContent
        title={isComplete ? 'Template applied' : 'Applying template'}
        height="max-sm"
        preventDismiss={isApplying}
        footer={
          <ModalFooterWithActions
            tertiaryAction={{ label: 'Cancel', onClick: onCancel, color: 'destructive' }}
            secondaryAction={
              isApplying
                ? {
                    label: 'Continue in new tab',
                    trailingIcon: <OpenNew className="h-4 w-4 shrink-0" aria-hidden />,
                    onClick: () => window.open(window.location.href, '_blank'),
                  }
                : undefined
            }
            primaryAction={
              onViewFile
                ? {
                    label: 'View File',
                    disabled: !isComplete || !outputFileId,
                    onClick: onViewFile,
                  }
                : undefined
            }
          />
        }
      >
        <div className="flex flex-col gap-4">
          <DocumentFile filename={templateName} type="template" status={isComplete ? 'complete' : 'in_progress'} />

          {/* Icon rail `w-10` matches `DocumentFile` so loader/success align with document icons. */}
          <div className="flex min-w-0 items-center gap-[10px]">
            <div className="flex w-10 shrink-0 items-center justify-center">
              <DotLoader loading={isApplyingActive} />
            </div>
            {isComplete ? (
              <div className="flex min-w-0 flex-1 items-center gap-1">
                <CheckCircle className="h-4 w-4 shrink-0 text-brand-neutral-950" aria-hidden />
                <span className="min-w-0 flex-1 text-sm italic tracking-[-0.15px] text-brand-neutral-950">{successLabel}</span>
              </div>
            ) : (
              <div className="flex min-w-0 flex-1 items-center gap-1">
                <SyncProgress className="h-4 w-4 shrink-0 text-brand-neutral-600" aria-hidden />
                <p className="min-w-0 flex-1 text-sm italic tracking-[-0.15px] text-brand-neutral-600" aria-live="polite">
                  {APPLY_TEMPLATE_STEP_MESSAGES[stepIndex]}
                  <AnimatedEllipsis />
                </p>
              </div>
            )}
          </div>

          <DocumentFile filename={outputFileName} type="file" status={isComplete ? 'complete' : 'in_progress'} />

          {isApplying && (
            <p className="text-sm font-medium leading-5 tracking-[-0.15px] text-brand-neutral-950">
              Keep this window open. Your file will be ready here when complete.
            </p>
          )}
        </div>
      </ModalContent>
    </Modal>
  )
}
